import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Import client logos
import federalMinistryFinanceLogo from '../images/clients/federal-ministry-finance.svg';
import nationalAssemblyLogo from '../images/clients/national-assembly-housing.svg';
import federalHousingLoanLogo from '../images/clients/federal-housing-loan.svg';
import nigeriaLabourCongressLogo from '../images/clients/nigeria-labour-congress.svg';
import nigeriaPoliceHousingLogo from '../images/clients/nigeria-police-housing.svg';

// Import Unique Solar Estate logo
import Logou from '../images/logo.png';

const EcumenicalLandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentImageType, setCurrentImageType] = useState<'products' | 'properties'>('products');

  // Hero slides data
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&h=640&q=80", 
      title: "Building Tomorrow's Infrastructure Today",
      subtitle: "Innovative construction and development services across Nigeria and Ghana"
    },
    {
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&h=640&q=80",
      title: "Years of Experience in Engineering Solutions",
      subtitle: "Tailored engineering solutions for residential, commercial and public buildings"
    },
    {
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&h=640&q=80",
      title: "Sustainable Development & Real Estate",
      subtitle: "Creating environmentally friendly engineering projects and premium properties"
    }
  ];

  // Auto-rotate slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  // Services data
  const services = [
    {
      icon: "🏗️",
      title: "Feasibility Studies",
      description: "Comprehensive financial feasibility, capital and running cost studies, site location investigations and technical possibility assessments."
    },
    {
      icon: "📐",
      title: "Design Services", 
      description: "From initial briefing through outline proposals, scheme planning, detailed design, working drawings, and contract documents."
    },
    {
      icon: "🏢",
      title: "Construction",
      description: "Environmentally friendly engineering projects with varying degrees of complexity using modern techniques and expertise."
    },
    {
      icon: "🔧",
      title: "Turnkey Services",
      description: "Prefabricated environmentally friendly engineering materials delivered as fully operational and ready for occupation."
    },
    {
      icon: "🛠️",
      title: "Maintenance",
      description: "Structural, electrical and mechanical maintenance and renovation services performed by our expert team."
    },
    {
      icon: "🏦",
      title: "Mortgage Services",
      description: "Partnering with leading Nigerian banks to offer easy access to facilities with rates as low as 6% to acquire homes."
    }
  ];

  // Products/Subsidiaries data
  const products = [
    {
      name: "Unique Solar Gardens Estate",
      image: "assets/Unique Solar Gardens Estate.jpg",
      description: "Sustainable living with solar-powered homes, 24/7 electricity, water supply, and security in SHEGO District, KARU.",
      link: "/solar-estate",
      category: "Real Estate"
    },
    {
      name: "Healthcare Solutions",
      image: "assets/Healthcare Solutions.png", 
      description: "Hospital and pharmacy development projects with modern medical facilities and equipment.",
      link: "#healthcare",
      category: "Healthcare"
    },
    {
      name: "Agricultural Services",
      image: "assets/Agriculture Services.png",
      description: "Easy access to agricultural loans and facilities supporting sustainable farming practices.",
      link: "#agriculture", 
      category: "Agriculture"
    },
    {
      name: "Infrastructure Development",
      image: "assets/Infrastructure Development.png",
      description: "Large-scale infrastructure projects including roads, bridges, and public facilities.",
      link: "#infrastructure",
      category: "Infrastructure"
    }
  ];

  // Properties for sale data
  const properties = [
    {
      image: "/assets/photos/9.jpg",
      title: "2 Bedroom Semi-detached Bungalow",
      location: "Unique Solar Gardens Estate, SHEGO District, KARU",
      features: "Solar powered, 24/7 electricity & water",
      price: "₦30.5 Million"
    },
    {
      image: "/assets/photos/10.jpg",
      title: "2 Bedroom Semi-detached Bungalow",
      location: "Unique Solar Gardens Estate, SHEGO District, KARU", 
      features: "Modern amenities, eco-friendly design",
      price: "₦30.5 Million"
    },
    {
      image: "/assets/photos/11.jpg",
      title: "Luxury 3 Bedroom Bungalow",
      location: "Unique Solar Gardens Estate, SHEGO District, KARU",
      features: "Premium finishes, spacious layout",
      price: "₦38 Million"
    },
    {
      image: "/assets/photos/12.jpg",
      title: "3 Bedroom Bungalow",
      location: "Unique Solar Gardens Estate, SHEGO District, KARU",
      features: "Quality construction, modern facilities",
      price: "₦38 Million"
    }
  ];

  // Team members data
  const teamMembers = [
    {
      name: "Dr. Emmanuel Okafor",
      position: "CEO & Managing Director",
      image: "assets/okafor.jpg",
      bio: "Over 20 years of experience in engineering and construction management."
    },
    {
      name: "Eng. Sarah Adebayo",
      position: "Head of Engineering",
      image: "assets/sarah2.jpg", 
      bio: "Specialized in structural engineering and sustainable construction practices."
    },
    {
      name: "Mr. David Mensah",
      position: "Project Manager",
      image: "assets/david.jpg",
      bio: "Expert in project coordination and quality assurance across multiple countries."
    }
  ];

  // Function to open image modal
  const openImageModal = (index: number, type: 'products' | 'properties') => {
    setCurrentImageIndex(index);
    setCurrentImageType(type);
    setImageModalOpen(true);
  };

  // Function to navigate images in modal
  const navigateImage = (direction: 'next' | 'prev') => {
    const currentArray = currentImageType === 'products' ? products : properties;
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % currentArray.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + currentArray.length) % currentArray.length);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Montserrat',sans-serif]">
      {/* Navigation */}
      <nav className="bg-white shadow-lg fixed w-full top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src={Logou} style={{width: '50px'}} alt="Ecumenical Investment & Development" />
              <div>
                <h1 className="text-xl font-bold text-primary">Ecumenical</h1>
                <p className="text-sm text-gray-600">Investment & Development</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className="text-gray-700 hover:text-primary transition-colors">Home</a>
              <a href="#services" className="text-gray-700 hover:text-primary transition-colors">Services</a>
              <a href="#products" className="text-gray-700 hover:text-primary transition-colors">Our Products</a>
              <a href="#about" className="text-gray-700 hover:text-primary transition-colors">About</a>
              <a href="#contact" className="text-gray-700 hover:text-primary transition-colors">Contact</a>
              <Link 
                to="/solar-estate" 
                className="bg-secondary hover:bg-secondary-dark text-white px-6 py-2 rounded-lg transition-colors"
                style={{color: '#222'}}
              >
                Visit Solar Estate
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                className="text-gray-700 hover:text-primary"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t">
              <div className="px-4 py-2 space-y-2">
                <a 
                  href="#home" 
                  className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">🏠</span>
                  Home
                </a>
                <a 
                  href="#services" 
                  className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">🔧</span>
                  Services
                </a>
                <a 
                  href="#products" 
                  className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">🏘️</span>
                  Our Products
                </a>
                <a 
                  href="#about" 
                  className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">ℹ️</span>
                  About
                </a>
                <a 
                  href="#contact" 
                  className="flex items-center gap-3 py-2 text-gray-700 hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">📞</span>
                  Contact
                </a>
                <Link 
                  to="/solar-estate" 
                  className="flex items-center gap-3 py-2 text-secondary font-medium hover:text-secondary-dark transition-colors text-black"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="text-lg">🌞</span>
                  Visit Solar Estate
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${slide.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-50"></div>
          </div>
        ))}
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-6xl font-bold mb-6 leading-tight">
            {heroSlides[currentSlide].title}
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto">
            {heroSlides[currentSlide].subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#services" 
              className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              Our Services
            </a>
            <a 
              href="#contact" 
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              Get In Touch
            </a>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-left">
              Comprehensive engineering solutions tailored to meet your development needs across Nigeria and Ghana
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">{service.icon}</div>
                <h3 className="text-2xl font-bold text-primary mb-4 group-hover:text-primary-dark transition-colors">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Our Products & Subsidiaries</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Diverse portfolio of development projects and services spanning multiple industries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
            {products.map((product, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100" onClick={() => openImageModal(index, 'products')} style={{cursor: 'pointer'}}>
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-pointer"
                    onClick={() => openImageModal(index, 'products')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-secondary to-primary text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                      {product.category}
                    </span>
                  </div>
                  {/* View Large Button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                   {/*  <button 
                      
                      className="bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-lg font-medium shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      View Large
                    </button> */}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-primary mb-3 group-hover:text-primary-dark transition-colors">{product.name}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>
                  <a 
                    href={product.link}
                    className="inline-flex items-center text-primary hover:text-secondary font-semibold transition-all duration-300 group-hover:translate-x-2"
                  >
                    Learn More 
                    <svg className="w-4 h-4 ml-2 group-hover:ml-4 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Properties for Sale Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Now Selling</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Premium properties available for immediate purchase across prime locations in Abuja
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {properties.map((property, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100"  onClick={() => openImageModal(index, 'properties')} style={{cursor: 'pointer'}}>
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={property.image} 
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 cursor-pointer"
                    onClick={() => openImageModal(index, 'properties')}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  {/* View Large Button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                   {/*  <button 
                     
                      className="bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-lg font-medium shadow-lg transform hover:scale-105 transition-all duration-300"
                    >
                      View Large
                    </button> */}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-primary mb-2 group-hover:text-primary-dark transition-colors">{property.title}</h3>
                  <div className="flex items-center text-gray-600 text-sm mb-1">
                    <svg className="w-4 h-4 mr-1 text-secondary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {property.location}
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{property.features}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">{property.price}</span>
                    <a 
                      href="#contact"
                      className="bg-gradient-to-r from-primary to-secondary hover:from-secondary hover:to-primary text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                      Contact Us
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">About Ecumenical</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Ecumenical Investment and Developmental Company Limited is a leading engineering and construction 
                company with years of experience in delivering tailored engineering solutions for the development 
                and construction of residential, commercial, and public buildings.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We are committed to maintaining a safe workplace and place high importance on the health and safety 
                of our employees, partners, contractors, subcontractors, suppliers, and host communities.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">20+</div>
                  <div className="text-gray-600">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">500+</div>
                  <div className="text-gray-600">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">2</div>
                  <div className="text-gray-600">Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-2">50+</div>
                  <div className="text-gray-600">Team Members</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="assets/About Ecumenical3.png" 
                alt="About Ecumenical"
                className="rounded-xl shadow-lg"
              />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-secondary rounded-xl opacity-20"></div>
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-primary rounded-xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Our Leadership Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the experienced professionals leading our company towards excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100">
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-100 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-8 text-center">
                  <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-primary-dark transition-colors">{member.name}</h3>
                  <p className="text-secondary font-semibold mb-4 text-sm uppercase tracking-wide">{member.position}</p>
                  <p className="text-gray-600 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Clients Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">Our Clients</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by leading government agencies and organizations across Nigeria and Ghana
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center">
            <div className="group flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                <img 
                  src={federalMinistryFinanceLogo} 
                  alt="Federal Ministry of Finance"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-sm font-semibold text-center text-gray-800 leading-tight">
                Federal Ministry of Finance Multipurpose Cooperative Society
              </h3>
            </div>

            <div className="group flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                <img 
                  src={nationalAssemblyLogo} 
                  alt="National Assembly Housing Cooperative Society"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-sm font-semibold text-center text-gray-800 leading-tight">
                National Assembly Housing Cooperative Society
              </h3>
            </div>

            <div className="group flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                <img 
                  src={federalHousingLoanLogo} 
                  alt="Federal Government Housing Loan Board"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-sm font-semibold text-center text-gray-800 leading-tight">
                Federal Government Housing Loan Board
              </h3>
            </div>

            <div className="group flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                <img 
                  src={nigeriaLabourCongressLogo} 
                  alt="Nigeria Labour Congress Cooperative Society"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-sm font-semibold text-center text-gray-800 leading-tight">
                Nigeria Labour Congress Cooperative Society
              </h3>
            </div>

            <div className="group flex flex-col items-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md">
                <img 
                  src={nigeriaPoliceHousingLogo} 
                  alt="Nigeria Police Housing Scheme"
                  className="w-16 h-16 object-contain"
                />
              </div>
              <h3 className="text-sm font-semibold text-center text-gray-800 leading-tight">
                Nigeria Police Housing Scheme
              </h3>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-6">
              We have successfully partnered with these esteemed organizations to deliver quality housing solutions and infrastructure projects.
            </p>
            <a 
              href="#contact"
              className="inline-flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Partner With Us
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* CSR Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
              Corporate Social Responsibility
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Ecumenical Investment and Developmental Co. Ltd. is committed to maintaining a safe workplace 
              and places higher importance on health and safety of its employees, partners, contractors, 
              subcontractors, suppliers and host community.
            </p>
            <a 
              href="#contact"
              className="inline-flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              Learn More About Our CSR
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-primary-dark text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 mt-4">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 pt-6">Get In Touch</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to start your next project? Contact us today for a consultation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Nigerian: +234 904 196 2781</p>
                      <p className="font-medium">Ghana: +233 240 129 607</p>
                      <p className="font-medium">UK: +44 7341188666</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">contact@ecumenical.com.ng</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Lagos, Nigeria & Accra, Ghana</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-xl font-bold mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <button className="w-12 h-12 bg-white bg-opacity-20 hover:bg-secondary rounded-lg flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>
                  <button className="w-12 h-12 bg-white bg-opacity-20 hover:bg-secondary rounded-lg flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </button>
                  <button className="w-12 h-12 bg-white bg-opacity-20 hover:bg-secondary rounded-lg flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white bg-opacity-10 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6">Send us a Message</h3>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary placeholder-gray-300"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary placeholder-gray-300"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary placeholder-gray-300"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Subject</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-3 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary placeholder-gray-300"
                    placeholder="Project Inquiry"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea 
                    rows={4}
                    className="w-full px-4 py-3 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary placeholder-gray-300"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>
                <button 
                  type="submit"
                  className="w-full bg-secondary hover:bg-secondary-dark text-white py-3 rounded-lg font-medium transition-colors"
                >
                  Send Message
                </button>
              </form>
            </div><br/>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20"></div>
          <div className="absolute top-0 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative z-10">
          {/* Main Footer Content */}
          <div className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
              {/* Company Info */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative">
                    <img src={Logou} style={{width: '60px', height: '60px'}} alt="Ecumenical Investment & Development" className="rounded-xl shadow-2xl" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-xl"></div>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Ecumenical</h1>
                    <p className="text-sm text-gray-400 font-medium">Investment & Development</p>
                  </div>
                </div>
                
                <p className="text-gray-300 leading-relaxed max-w-md text-lg">
                  Leading engineering and construction company delivering tailored solutions 
                  for residential, commercial, and public buildings across Nigeria and Ghana.
                </p>

                {/* Key Stats */}
                <div className="grid grid-cols-2 gap-6 pt-4">
                  <div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10">
                    <div className="text-2xl font-bold text-secondary mb-1">20+</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Years Experience</div>
                  </div>
                  <div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10">
                    <div className="text-2xl font-bold text-secondary mb-1">500+</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Projects Completed</div>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white relative pb-2">
                  Quick Links
                  <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                </h3>
                <ul className="space-y-3">
                  {[
                    { label: 'Home', href: '#home' },
                    { label: 'Services', href: '#services' },
                    { label: 'Our Products', href: '#products' },
                    { label: 'About Us', href: '#about' },
                    { label: 'Contact', href: '#contact' }
                  ].map((link) => (
                    <li key={link.label}>
                      <a 
                        href={link.href} 
                        className="text-gray-400 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block group"
                      >
                        <span className="relative">
                          {link.label}
                          <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></div>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Our Products */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white relative pb-2">
                  Our Products
                  <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                </h3>
                <ul className="space-y-3">
                  {[
                    { label: 'Solar Gardens Estate', component: Link, href: '/solar-estate' },
                    { label: 'Healthcare Solutions', component: 'a', href: '#healthcare' },
                    { label: 'Agricultural Services', component: 'a', href: '#agriculture' },
                    { label: 'Infrastructure', component: 'a', href: '#infrastructure' }
                  ].map((item) => (
                    <li key={item.label}>
                      {item.component === Link ? (
                        <Link 
                          to={item.href} 
                          className="text-gray-400 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block group"
                        >
                          <span className="relative">
                            {item.label}
                            <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></div>
                          </span>
                        </Link>
                      ) : (
                        <a 
                          href={item.href} 
                          className="text-gray-400 hover:text-white transition-all duration-300 hover:translate-x-2 inline-block group"
                        >
                          <span className="relative">
                            {item.label}
                            <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></div>
                          </span>
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Info & Social Links */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16 pt-12 border-t border-white/10">
              {/* Contact Information */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white relative pb-2">
                  Get In Touch
                  <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 group">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="space-y-1">
                      <p className="font-semibold text-white">Phone Numbers</p>
                      <p className="text-gray-300 text-sm">Nigeria: +234 904 196 2781</p>
                      <p className="text-gray-300 text-sm">Ghana: +233 240 129 607</p>
                      <p className="text-gray-300 text-sm">UK: +44 7341188666</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 group">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Email</p>
                      <a href="mailto:contact@ecumenical.com.ng" className="text-gray-300 hover:text-secondary transition-colors">
                        contact@ecumenical.com.ng
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 group">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-white">Locations</p>
                      <p className="text-gray-300">Lagos, Nigeria & Accra, Ghana</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white relative pb-2">
                  Follow Us
                  <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                </h3>
                
                <p className="text-gray-300 mb-6">Stay connected with us on social media for the latest updates and project showcases.</p>
                
                <div className="flex space-x-4">
                  {[
                    { 
                      icon: (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      ),
                      name: 'Facebook',
                      color: 'hover:bg-blue-600'
                    },
                    { 
                      icon: (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                      ),
                      name: 'Twitter',
                      color: 'hover:bg-sky-500'
                    },
                    { 
                      icon: (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      ),
                      name: 'LinkedIn',
                      color: 'hover:bg-blue-700'
                    },
                    { 
                      icon: (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      ),
                      name: 'Instagram',
                      color: 'hover:bg-pink-600'
                    }
                  ].map((social, index) => (
                    <button 
                      key={index}
                      className={`w-14 h-14 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1 hover:shadow-xl ${social.color} group`}
                      aria-label={social.name}
                    >
                      <div className="text-white group-hover:scale-110 transition-transform duration-300">
                        {social.icon}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 bg-black/20 backdrop-blur-sm">
            <div className="container mx-auto px-4 py-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <div className="text-gray-400 text-center md:text-left">
                  <p>&copy; 2025 Ecumenical Investment and Developmental Company Limited. All rights reserved.</p>
                </div>
                <div className="flex flex-wrap justify-center gap-6 text-sm">
                  <a href="#privacy" className="text-gray-400 hover:text-white transition-colors hover:underline">Privacy Policy</a>
                  <a href="#terms" className="text-gray-400 hover:text-white transition-colors hover:underline">Terms of Service</a>
                  <a href="#cookies" className="text-gray-400 hover:text-white transition-colors hover:underline">Cookie Policy</a>
                  <a href="#sitemap" className="text-gray-400 hover:text-white transition-colors hover:underline">Sitemap</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Image Modal */}
      {imageModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-90 p-4">
          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Previous Button */}
            <button
              onClick={() => navigateImage('prev')}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={() => navigateImage('next')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image */}
            <div className="relative max-w-full max-h-full">
              <img
                src={currentImageType === 'products' ? products[currentImageIndex].image : properties[currentImageIndex].image}
                alt={currentImageType === 'products' ? products[currentImageIndex].name : properties[currentImageIndex].title}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
              
              {/* Image Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                <h3 className="text-white text-xl font-bold mb-2">
                  {currentImageType === 'products' ? products[currentImageIndex].name : properties[currentImageIndex].title}
                </h3>
                <p className="text-gray-200 text-sm">
                  {currentImageType === 'products' ? products[currentImageIndex].description : properties[currentImageIndex].location}
                </p>
                {currentImageType === 'properties' && (
                  <p className="text-secondary font-bold text-lg mt-2">
                    {properties[currentImageIndex].price}
                  </p>
                )}
              </div>
            </div>

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full">
              {currentImageIndex + 1} / {currentImageType === 'products' ? products.length : properties.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcumenicalLandingPage;
